<script setup lang="ts">
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  apiErrorMessage,
  useDeviceTypes,
} from '@/composables/equipment/useEquipmentLookups';
import { EquipmentService } from '@/services/equipment.services';
import type { IDevice, IDeviceGroup } from '@/types/equipment.types';
import { typeIcon } from '@/utils/equipmentIcons';
import { moveItem, placementOf } from '@/utils/equipmentForm';
import EquipmentField from './EquipmentField.vue';
import EquipmentModal from './EquipmentModal.vue';
import EquipmentSelect from './EquipmentSelect.vue';
import EquipmentSiteSelect from './EquipmentSiteSelect.vue';
import EquipmentStoreSelect from './EquipmentStoreSelect.vue';

const props = defineProps<{ group?: IDeviceGroup; lockedStoreId?: string }>();
const emit = defineEmits<{ close: []; saved: [] }>();

const { t } = useI18n({ useScope: 'global' });
const queryClient = useQueryClient();
const { typeOf, typeLabel } = useDeviceTypes();
const editing = computed(() => !!props.group);

const form = reactive({
  storeId: props.group?.store.id ?? props.lockedStoreId ?? '',
  siteId: props.group?.site.id ?? '',
  name: props.group?.name ?? '',
  masterId: props.group?.master.id ?? '',
  memberIds: (props.group?.members ?? []).map((m) => m.id),
});
const toAdd = ref('');
const busy = ref(false);
const error = ref('');

// Les appareils du site, retirés exclus : un sous-groupe ne se compose que de ce qui est en service ou prévu.
const { data: siteDevices } = useQuery({
  queryKey: computed(() => ['equipment', 'group-devices', form.siteId]),
  queryFn: () =>
    EquipmentService.searchDevices({
      page: 1,
      pageSize: 200,
      siteIds: [form.siteId],
      statuses: ['planned', 'installed'],
      sortBy: 'label',
      sortOrder: 'ASC',
    }),
  enabled: computed(() => !!form.siteId),
});
const devices = computed<IDevice[]>(() =>
  form.siteId ? (siteDevices.value?.data ?? []) : [],
);
const byId = (id: string) => devices.value.find((d) => d.id === id);

const masters = computed(() =>
  devices.value.filter((d) => typeOf(d.type)?.master),
);
const members = computed(() =>
  form.memberIds.map((id) => byId(id)).filter((d): d is IDevice => !!d),
);
const addable = computed(() =>
  devices.value.filter(
    (d) => d.id !== form.masterId && !form.memberIds.includes(d.id),
  ),
);

// Changer de commerce ou de site vide ce qui en dépend : un maître d'un autre site serait refusé.
const onStoreChange = () => {
  form.siteId = '';
  onSiteChange();
};
const onSiteChange = () => {
  form.masterId = '';
  form.memberIds = [];
};
watch(
  () => form.masterId,
  (id) => {
    form.memberIds = form.memberIds.filter((m) => m !== id);
  },
);

const add = () => {
  if (toAdd.value) form.memberIds = [...form.memberIds, toAdd.value];
  toAdd.value = '';
};
const remove = (id: string) => {
  form.memberIds = form.memberIds.filter((m) => m !== id);
};
const move = (index: number, by: -1 | 1) => {
  form.memberIds = moveItem(form.memberIds, index, by);
};
const placementLabel = (index: number) => {
  const placement = placementOf(members.value, index);
  return placement.kind === 'default'
    ? t('equipment.groups.default')
    : t('equipment.groups.fallback', { n: placement.number });
};

const deviceOption = (device: IDevice) => ({
  value: device.id,
  label: device.label,
  description: typeLabel(device.type),
  icon: typeIcon(device.type),
});
const masterOptions = computed(() => masters.value.map(deviceOption));
// Une liste vide dit pourquoi elle l'est : pas de site choisi, ou aucun appareil capable d'être maître sur ce site.
const masterPlaceholder = computed(() => {
  if (!form.siteId) return t('equipment.groups.chooseSiteFirst');
  return masterOptions.value.length
    ? t('equipment.groups.chooseMaster')
    : t('equipment.groups.noMasterOnSite');
});
const addOptions = computed(() => addable.value.map(deviceOption));

const canSave = computed(
  () => !!form.siteId && !!form.name.trim() && !!form.masterId && !busy.value,
);

const save = async () => {
  if (!canSave.value) return;
  busy.value = true;
  error.value = '';
  const body = {
    name: form.name.trim(),
    masterDeviceId: form.masterId,
    memberIds: form.memberIds,
  };
  try {
    if (props.group)
      await EquipmentService.updateGroup(
        props.group.store.id,
        props.group.site.id,
        props.group.id,
        body,
      );
    else await EquipmentService.createGroup(form.storeId, form.siteId, body);
    await queryClient.invalidateQueries({ queryKey: ['equipment'] });
    emit('saved');
  } catch (failure) {
    error.value = apiErrorMessage(failure, t('equipment.errors.generic'));
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <EquipmentModal
    wide
    :title="editing ? t('equipment.groups.edit') : t('equipment.groups.create')"
    @close="emit('close')"
  >
    <div class="gfm__row">
      <EquipmentField :label="t('equipment.fields.store')">
        <EquipmentStoreSelect
          v-model="form.storeId"
          :disabled="editing || !!lockedStoreId"
          @update:model-value="onStoreChange"
        />
      </EquipmentField>
      <EquipmentField :label="t('equipment.fields.site')">
        <EquipmentSiteSelect
          v-model="form.siteId"
          :store-id="form.storeId"
          :disabled="editing"
          @update:model-value="onSiteChange"
        />
      </EquipmentField>
    </div>

    <EquipmentField
      :label="t('equipment.fields.groupName')"
      :hint="t('equipment.groups.nameHint')"
    >
      <input v-model="form.name" class="eq-input" type="text" maxlength="100" />
    </EquipmentField>

    <EquipmentField
      :label="t('equipment.groups.master')"
      :hint="t('equipment.groups.masterHint')"
    >
      <EquipmentSelect
        v-model="form.masterId"
        :options="masterOptions"
        :placeholder="masterPlaceholder"
        :disabled="!form.siteId"
      />
    </EquipmentField>

    <section class="gfm__members">
      <h3 class="gfm__heading">{{ t('equipment.groups.members') }}</h3>
      <p class="eq-muted gfm__hint">{{ t('equipment.groups.membersHint') }}</p>
      <ol v-if="members.length" class="gfm__list">
        <li
          v-for="(member, index) in members"
          :key="member.id"
          class="gfm__item"
        >
          <span class="gfm__name">
            <strong>{{ member.label }}</strong>
            <span class="eq-muted"> · {{ typeLabel(member.type) }}</span>
          </span>
          <span class="eq-badge">{{ placementLabel(index) }}</span>
          <button
            type="button"
            class="eq-btn eq-btn--icon"
            :disabled="index === 0"
            :aria-label="t('equipment.groups.moveUp')"
            @click="move(index, -1)"
          >
            <i class="pi pi-arrow-up" />
          </button>
          <button
            type="button"
            class="eq-btn eq-btn--icon"
            :disabled="index === members.length - 1"
            :aria-label="t('equipment.groups.moveDown')"
            @click="move(index, 1)"
          >
            <i class="pi pi-arrow-down" />
          </button>
          <button
            type="button"
            class="eq-btn eq-btn--icon"
            :aria-label="t('equipment.groups.remove')"
            @click="remove(member.id)"
          >
            <i class="pi pi-times" />
          </button>
        </li>
      </ol>
      <p v-else class="eq-muted">{{ t('equipment.groups.noMembers') }}</p>

      <div class="gfm__add">
        <EquipmentSelect
          v-model="toAdd"
          class="gfm__pick"
          :options="addOptions"
          :placeholder="t('equipment.groups.addDevice')"
          :disabled="!addable.length"
        />
        <button type="button" class="eq-btn" :disabled="!toAdd" @click="add">
          <i class="pi pi-plus" /> {{ t('equipment.groups.add') }}
        </button>
      </div>
    </section>

    <p v-if="error" class="eq-error" role="alert">{{ error }}</p>
    <template #footer>
      <button type="button" class="eq-btn" @click="emit('close')">
        {{ t('equipment.cancel') }}
      </button>
      <button
        type="button"
        class="eq-btn eq-btn--primary"
        :disabled="!canSave"
        @click="save"
      >
        {{ t('equipment.save') }}
      </button>
    </template>
  </EquipmentModal>
</template>

<style scoped>
.gfm__row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
  gap: 0.75rem;
}
.gfm__heading {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.gfm__hint {
  margin: 0.15rem 0 0.5rem;
  font-size: 0.8125rem;
}
.gfm__list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin: 0 0 0.75rem;
  padding: 0;
  list-style: none;
}
.gfm__item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.6rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
}
.gfm__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.gfm__add {
  display: flex;
  gap: 0.5rem;
}
.gfm__pick {
  flex: 1;
}
</style>
